import {
    ButtonGradientPrimary,
    ButtonOutlinedPrimary,
    ButtonSecondary,
    Colors,
    Icon,
} from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

interface StepNavigationProps {
    /** True on the last step, where the primary button creates the meeting instead of advancing. */
    isLastStep: boolean;
    canGoBack: boolean;
    isNextDisabled: boolean;
    isCreating: boolean;
    onBack: () => void;
    onCreate: () => void;
}

/**
 * Footer of the creation flow. The forward button is the form's submit button, so Enter in a step
 * advances to the next one. The last step's button is a plain button that creates the meeting, so a
 * submit reaching the footer from anywhere never creates anything on its own.
 */
/** The design sets the flow's navigation in 12px, smaller than the library's buttons. */
const STEP_BUTTON_TEXT = 'text-[12px]';

export const StepNavigation = ({
    isLastStep,
    canGoBack,
    isNextDisabled,
    isCreating,
    onBack,
    onCreate,
}: StepNavigationProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex justify-end gap-3'}>
            {canGoBack && (
                <ButtonSecondary
                    type={'button'}
                    onClick={onBack}
                    size={'md'}
                    className={STEP_BUTTON_TEXT}
                    leftIcon={
                        <Icon
                            name={'arrow-arrow-left'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={'new-meeting-back-button'}
                >
                    {t('thematicMeetings.newMeeting.back')}
                </ButtonSecondary>
            )}

            {isLastStep ? (
                <ButtonGradientPrimary
                    type={'button'}
                    onClick={onCreate}
                    disabled={isCreating}
                    loading={isCreating}
                    size={'md'}
                    className={STEP_BUTTON_TEXT}
                    rightIcon={
                        <Icon
                            name={'time-calendar-tick'}
                            size={'sm'}
                            color={Colors.WHITE}
                            ariaHidden
                        />
                    }
                    dataTestId={'new-meeting-create-button'}
                >
                    {isCreating
                        ? t('thematicMeetings.newMeeting.creating')
                        : t('thematicMeetings.newMeeting.create')}
                </ButtonGradientPrimary>
            ) : (
                // Outlined, not gradient: only creating the meeting is the flow's primary action.
                <ButtonOutlinedPrimary
                    type={'submit'}
                    disabled={isNextDisabled}
                    size={'md'}
                    className={STEP_BUTTON_TEXT}
                    borderColor={'primary'}
                    textColor={'base'}
                    rightIcon={
                        <Icon
                            name={'arrow-arrow-right'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={'new-meeting-next-button'}
                >
                    {t('thematicMeetings.newMeeting.nextStep')}
                </ButtonOutlinedPrimary>
            )}
        </div>
    );
};
